import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AdminFormDialog from '../../components/admin/AdminFormDialog';
import {
  ConfirmDialog,
  DataTable,
  InlineAlert,
  LoadingState,
  PageHeader,
  Toast,
} from '../../components/admin/ui';

const AdminsManagement = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', tone: 'success' });

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/admin/admins');
      setAdmins(response.data.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleDelete = async () => {
    if (!confirmId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/admins/${confirmId}`);
      setConfirmId(null);
      setToast({ open: true, message: 'Administrador eliminado', tone: 'success' });
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      setToast({
        open: true,
        message: error.response?.data?.message || 'Error al eliminar el administrador',
        tone: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingState />;

  const columns = [
    {
      key: 'name',
      header: 'Nombre',
      render: (row) => `${row.first_name} ${row.last_name}`,
    },
    {
      key: 'username',
      header: 'Usuario',
      render: (row) => <span className="font-mono text-sm">{row.username}</span>,
    },
    { key: 'email', header: 'Email' },
    {
      key: 'created_at',
      header: 'Registro',
      render: (row) => new Date(row.created_at).toLocaleDateString('es-AR'),
    },
    {
      key: 'actions',
      header: 'Acciones',
      className: 'text-right',
      render: (row) => (
        <div className="flex justify-end gap-1">
          <button
            type="button"
            className="btn-admin-ghost !min-h-9"
            onClick={() => {
              setSelectedAdmin(row);
              setDialogOpen(true);
            }}
          >
            Editar
          </button>
          <button
            type="button"
            className="btn-admin-ghost !min-h-9 text-danger"
            onClick={() => setConfirmId(row.id)}
          >
            Eliminar
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <PageHeader
        title="Administradores"
        onBack={() => navigate('/admin')}
        action={
          <button
            type="button"
            className="btn-admin"
            onClick={() => {
              setSelectedAdmin(null);
              setDialogOpen(true);
            }}
          >
            Agregar
          </button>
        }
      />

      <DataTable columns={columns} rows={admins} emptyTitle="No hay administradores" />

      <InlineAlert tone="warning" className="mt-6">
        No se puede eliminar el último administrador ni eliminar tu propia cuenta.
      </InlineAlert>

      <AdminFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedAdmin(null);
        }}
        admin={selectedAdmin}
        onSuccess={fetchAdmins}
      />

      <ConfirmDialog
        open={Boolean(confirmId)}
        title="Eliminar administrador"
        message="¿Estás seguro de eliminar este administrador?"
        confirmLabel="Eliminar"
        danger
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setConfirmId(null)}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        tone={toast.tone}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </div>
  );
};

export default AdminsManagement;
